import { useState, type ChangeEventHandler, type FC } from 'react';
import { BsPlus } from 'react-icons/bs';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';

import { type TimelineSlide } from '@editor2/types/pageElements';

import { slidesPanel } from './styles';
import IconButton from '@editor2/components/IconButton';
import { type FormControlElement } from '@editor2/types/formControl';
import { Form } from 'react-bootstrap';

interface Props {
	slides: TimelineSlide[];
	startSlideIndex?: number;
	titleSlide?: TimelineSlide;
	activeSlideIndex: number;
	setActiveSlideIndex: (_) => void;
	onAddSlideClick: () => void;
	onAddTitleSlideClick: () => void;
	onSlidesReorder: (_) => void;
	onChangeStartSlideIndex: (index: number) => void;
}

const SlidesPanel: FC<Props> = (props) => {
	const {
		slides,
		startSlideIndex,
		titleSlide,
		activeSlideIndex,
		setActiveSlideIndex,
		onAddSlideClick,
		onAddTitleSlideClick,
		onSlidesReorder,
		onChangeStartSlideIndex
	} = props;
	/**
	 * `startSlide` is 1-indexed and refers to the slide number that the Timeline should start on.
	 * Importantly, the title slide is not counted (so `startSlide === 1` means the first data slide,
	 * regardless of whether there is a title slide or not).
	 *
	 * `startSlideIndex` maps directly to TimelineJS's `start_at_slide`, which is always 0-indexed
	 * (`startSlideIndex === 0` means the title slide, if there is one, or the first data slide otherwise).
	 *
	 * We use a string state variable and control the input so that we can present 1-indexing
	 * while implementing validation; if the value is invalid we don't call the onChange handler.
	 */
	const [startSlide, setStartSlide] = useState(
		startSlideIndex ? `${startSlideIndex + (titleSlide ? 0 : 1)}` : ''
	);

	const onTitleSlideClick = () => {
		if (!titleSlide) onAddTitleSlideClick();
		setActiveSlideIndex(-1);
	};

	const onDragEnd = (result) => {
		const { source, destination, draggableId, type } = result;
		const { droppableId: sourceDroppableId, index: sourceIndex } = source;
		const { droppableId: destinationDroppableId, index: destinationIndex } = destination;

		if (sourceDroppableId !== destinationDroppableId) return;

		const draggableSlide = slides[sourceIndex];
		const tempSlides = [...slides.slice(0, sourceIndex), ...slides.slice(sourceIndex + 1)];
		const updatedSlides = [
			...tempSlides.slice(0, destinationIndex),
			draggableSlide,
			...tempSlides.slice(destinationIndex)
		];

		onSlidesReorder(updatedSlides);
	};

	const handleChangeStartSlide: ChangeEventHandler<FormControlElement> = (e) => {
		setStartSlide(e.target.value);

		if (!e.target.value.trim()) {
			onChangeStartSlideIndex(null);
			return;
		}

		const i = parseInt(e.target.value, 10);
		if (!Number.isNaN(i) && i > 0 && i <= slides.length) {
			onChangeStartSlideIndex(i - (titleSlide ? 0 : 1));
		}
	};

	return (
		<div className={slidesPanel}>
			<div>
				<span className="label">Title slide (optional)</span>
				<div
					className={`slideWrapper titleSlideWrapper ${activeSlideIndex === -1 && 'activeSlide'}`}>
					<div className={`headline`} onClick={() => onTitleSlideClick()}>
						<span className="slideNumber">*</span>

						{titleSlide && <div>{titleSlide.text?.headline}</div>}
						{!titleSlide && <div className="infoText">Click to add title slide</div>}
					</div>
				</div>

				<Form.Group>
					<Form.Label>Start slide (optional)</Form.Label>
					<Form.Control
						type="number"
						min={1}
						max={slides.length}
						value={startSlide}
						onChange={handleChangeStartSlide}
						isInvalid={
							startSlide &&
							(parseInt(startSlide, 10) < 1 || parseInt(startSlide, 10) > slides.length)
						}
					/>
				</Form.Group>

				<div className="slidesHeaderBlock">
					<span className="label">Slides ({slides.length} total)</span>
					<span className="addSlideButton">
						<IconButton onClick={onAddSlideClick}>
							<BsPlus />
						</IconButton>
					</span>
				</div>
			</div>

			<div className="scrollableBlock">
				<DragDropContext onDragEnd={onDragEnd}>
					<DelayedDroppableWrapper droppableId={'slides-panel'} type="TimelineSlide">
						{(provided) => (
							<div ref={provided.innerRef} {...provided.droppableProps}>
								{slides.map((slide, ind) => (
									<Draggable draggableId={slide.unique_id} index={ind} key={slide.unique_id}>
										{(provided) => (
											<div ref={provided.innerRef} {...provided.draggableProps}>
												<div
													{...provided.dragHandleProps}
													className={`slideWrapper ${activeSlideIndex === ind && 'activeSlide'}`}>
													<span className="slideNumber">{ind + 1}</span>

													<div className="headline" onClick={() => setActiveSlideIndex(ind)}>
														{slide.text?.headline} [{slide.start_date?.year}]
													</div>
												</div>
											</div>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</DelayedDroppableWrapper>
				</DragDropContext>
			</div>
		</div>
	);
};

export default SlidesPanel;
